import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CancelOperationResponse,
  HistoryItem,
} from '../models/trading.interface';
import {
  CouponRedeemRequest,
  CouponRedeemResponse,
} from '../models/coupon.model';

@Injectable({
  providedIn: 'root',
})
export class TradingService {
  private apiUrl = environment.baseUrl + 'operations/';
  private userApiUrl = environment.baseUrl + 'user/';
  private readonly azureBlobHostnameSuffix = '.blob.core.windows.net';

  private operationTokenSubject = new BehaviorSubject<string | null>(null);
  operationToken$ = this.operationTokenSubject.asObservable();

  constructor(private http: HttpClient) {}

  setOperationToken(token: string | null) {
    this.operationTokenSubject.next(token);
  }

  getOperationToken(): Observable<string | null> {
    return this.operationToken$;
  }

  getDollarExchange() {
    return this.http.get(this.apiUrl + 'exchange-rate');
  }

  getHistory(
    page: number,
    size: number,
    operation: string = '',
    status: string = '',
    isScheduled?: boolean,
  ) {
    const params: Record<string, string> = {
      page: page.toString(),
      size: size.toString(),
      operation,
      status,
    };
    if (isScheduled === true) {
      params['isScheduled'] = 'true';
    }
    return this.http.get<HistoryItem[]>(
      `${this.apiUrl}history?${new URLSearchParams(params).toString()}`,
      {
        observe: 'response',
      },
    );
  }

  getHistoryItem(itemId: string) {
    return this.http.get(this.apiUrl + 'history/' + itemId);
  }

  doQuotation(
    walletNumber: string,
    tradingType: string,
    accountBankId?: number,
    walletNumberDest?: string,
    isScheduled?: boolean,
  ) {
    const params: Record<string, unknown> = isScheduled
      ? { walletNumber, typeOperation: tradingType, isScheduled: true }
      : {
          walletNumber,
          typeOperation: tradingType,
          accountBankId,
          walletNumberDest,
        };
    return this.http.post(this.apiUrl + 'quotation', params);
  }

  updateQuotation(amount: number, limitPrice?: number) {
    console.log('limitPrice', limitPrice);
    const params: Record<string, number> = {
      amountPrice: amount,
    };
    if (limitPrice != null) {
      params['limitPrice'] = limitPrice;
    }
    const opToken = this.operationTokenSubject.value;
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json',
    };
    if (opToken) {
      headers['Token-Operation'] = opToken.startsWith('Bearer ')
        ? opToken
        : `Bearer ${opToken}`;
    }
    return this.http.put(this.apiUrl + 'quotation', params, { headers });
  }

  sendOperationOtp() {
    return this.http.post(this.apiUrl + 'send-otp', {});
  }

  validateOperationOtp(otp: string) {
    const params = {
      channel: 'SMS',
      otpNumber: otp,
    };
    return this.http.put(this.apiUrl + 'valid-otp', params);
  }

  /**
   * Confirma la operación. En compra con voucher se pueden enviar
   * `operationNumber`, `depositedAt` (`YYYY-MM-DD`) y, si el usuario
   * validó un cupón, `couponCode`. En venta/conversión el body va vacío.
   */
  confirmOperation(payload?: {
    operationNumber?: string;
    depositedAt?: string;
    couponCode?: string;
  }) {
    const body: {
      operationNumber?: string;
      depositedAt?: string;
      couponCode?: string;
    } = {};
    if (payload?.operationNumber) {
      body.operationNumber = payload.operationNumber;
    }
    if (payload?.depositedAt) {
      body.depositedAt = payload.depositedAt;
    }
    const couponCode = payload?.couponCode?.trim();
    if (couponCode) {
      body.couponCode = couponCode;
    }
    return this.http.post(this.apiUrl + 'confirmation', body);
  }

  /**
   * Valida un cupón WALLET en el flujo de compra.
   * El registro ocurre al confirmar, con `couponCode` en
   * `POST operations/confirmation`.
   * `POST user/coupon/redeem`
   */
  redeemCoupon(payload: CouponRedeemRequest): Observable<CouponRedeemResponse> {
    return this.http.post<CouponRedeemResponse>(
      `${this.userApiUrl}coupon/redeem`,
      payload,
    );
  }

  cancelScheduledOperation(operationId: string) {
    return this.http.put<CancelOperationResponse>(
      `${this.apiUrl}${operationId}/cancel`,
      {},
    );
  }

  async uploadFile(sasUrl: string, file: File): Promise<any> {
    const currentDate = new Date().toLocaleDateString('en-CA', {
      timeZone: 'America/Lima',
    });

    if (!file) {
      throw new Error('No file provided');
    }
    if (!sasUrl) {
      throw new Error('No SAS URL provided');
    }
    if (!file.type.startsWith('image/')) {
      throw new Error('File type not supported');
    }

    let url: URL;
    try {
      url = new URL(sasUrl);
      const normalizedHostname = url.hostname.toLowerCase();

      if (url.protocol !== 'https:') {
        throw new Error('Only HTTPS URLs are allowed for file uploads');
      }
      if (!normalizedHostname.endsWith(this.azureBlobHostnameSuffix)) {
        throw new Error('Invalid SAS URL: must be from Azure Blob Storage');
      }
    } catch (error) {
      throw new Error(
        `Invalid SAS URL: ${error instanceof Error ? error.message : 'URL validation failed'}`,
      );
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.timeout = 30000;
      xhr.open('PUT', sasUrl, true);

      // Required Azure headers
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.setRequestHeader('x-ms-meta-date', currentDate);

      // No Authorization or custom tokens needed for SAS upload

      xhr.onload = () => {
        if (xhr.status === 201) {
          resolve(xhr.response);
        } else {
          reject(
            new Error(`Error uploading file: ${xhr.status} ${xhr.statusText}`),
          );
        }
      };

      xhr.onerror = () =>
        reject(new Error('Network error occurred during file upload'));
      xhr.ontimeout = () => reject(new Error('File upload timed out'));

      xhr.send(file);
    });
  }
}
